import Store from '../store'
import axios from 'axios'
import Cookies from 'universal-cookie'
import fileDownload from 'js-file-download'
import { token_keys } from '../constants'
import { axiosInstance, refreshAccessToken } from "./axiosInstance";

const cookies = new Cookies();

const getHeaders = (includeAuth) => {
  const headers = {
    'X-CSRFToken': cookies.get(token_keys.csrf_token),
  };

  if (includeAuth) {
    headers.Authorization = `Bearer ${localStorage.getItem(token_keys.access_token)}`;
  }

  return headers;
};

// ----------- Helpers for API requests

const handleError = (error) => {
  const errorData = error.response?.data || {};
  let errorMessage = 'An unexpected error occurred.';
  let fieldErrors = null;

  if (typeof errorData === 'object' && !Array.isArray(errorData)) {
    if (errorData.error) {
      errorMessage = errorData.error;
    } else if (errorData.message) {
      errorMessage = errorData.message;
    } else {
      Object.keys(errorData).forEach((field) => {
        if (Array.isArray(errorData[field])) {
          if (!fieldErrors) fieldErrors = {};
          fieldErrors[field] = errorData[field][0];
        }
      });
      if (fieldErrors && Object.keys(fieldErrors).length > 0) {
        errorMessage = fieldErrors[Object.keys(fieldErrors)[0]] || errorMessage;
      }
    }
  } else if (error.message) {
    errorMessage = error.message;
  }

  const response = {
    success: false,
    error: errorMessage,
    data: errorData,
    status: error.response?.status || null,
    retryAfter: error.response?.headers?.['retry-after'],
  };

  if (fieldErrors) {
    response.fieldErrors = fieldErrors;
  }

  return response;
};

const makeGetRequest = async (url, includeAuth = false, extraHeaders = {}) => {
  try {
    const headers = { ...getHeaders(includeAuth), ...extraHeaders };
    const res = await axiosInstance.get(url, { headers });

    return {
      success: true,
      data: res.data,
      status: res.status,
    };
  } catch (e) {
    return handleError(e);
  }
};

const makePostRequest = async (url, body, includeAuth = false, contentType = '', extraHeaders = {}) => {
  try {
    const headers = { ...getHeaders(includeAuth), ...extraHeaders };
    if (contentType === 'json') {
      headers['Content-Type'] = 'application/json';
    } else {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }
    const res = await axiosInstance.post(url, body, {
      headers,
    });

    return {
      success: true,
      data: res.data,
      status: res.status,
    };
  } catch (e) {
    return handleError(e);
  }
};

const makePostFormRequest = async (url, formData, includeAuth = false) => {
  try {
    const headers = getHeaders(includeAuth);
    headers['Content-Type'] = 'multipart/form-data';

    const res = await axiosInstance.post(url, formData, {
      headers,
    });

    return {
      success: true,
      data: res.data,
      status: res.status,
    };
  } catch (e) {
    return handleError(e);
  }
};

const makePatchRequest = async (url, body, includeAuth = false) => {
  try {
    const headers = getHeaders(includeAuth);
    headers['Content-Type'] = 'application/json';

    const res = await axiosInstance.patch(url, body, {
      headers,
    });

    return {
      success: true,
      data: res.data,
      status: res.status,
    };
  } catch (e) {
    return handleError(e);
  }
};

const makePutRequest = async (url, body, includeAuth = false) => {
  try {
    const headers = getHeaders(includeAuth);
    headers['Content-Type'] = 'application/json';

    const res = await axiosInstance.put(url, body, {
      headers,
    });

    return {
      success: true,
      data: res.data,
      status: res.status,
    };
  } catch (e) {
    return handleError(e);
  }
};

const makeDeleteRequest = async (url, includeAuth = false, data = null) => {
  try {
    const headers = getHeaders(includeAuth);
    const config = {
      headers,
    };

    if (data) {
      config.data = data;
    }

    const res = await axiosInstance.delete(url, config);

    return {
      success: true,
      data: res.data,
      status: res.status,
    };
  } catch (e) {
    return handleError(e);
  }
};


const getCSRF = () =>
  axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/csrf/`).then(
    (res) => {
      Store.setState({ csrf: res.data.csrfToken })
      console.log({ res })
    },
    (err) => {
      console.log('err is : ', err)
    }
  )

// ----------- Login and Registration

const registerUser = async (userData) => {
  const response = await makePostRequest(`/accounts/register/`, userData, false, 'json')
  return response
}

const login = async (userData) => {
  const response = await makePostRequest(`/accounts/login/`, userData)
  return response
}

const authLookup = async (email) => {
  const response = await makePostRequest(`/accounts/auth-lookup/`, { email }, false, 'json')
  return response
}

const ssoStart = async (email) => {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/v1/accounts/auth/sso/start/`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { success: false, error: data?.error || data?.message || 'Unable to start SSO', data, status: res.status };
    }
    return { success: true, data, status: res.status };
  } catch (e) {
    return { success: false, error: e.message || 'Network error', data: {}, status: null };
  }
}

const logout = async (accessToken, localOnly = false) => {
  // Unified logout endpoint. Default (local_only omitted/false) performs a full
  // federated logout; for Entra users the response carries a `redirect_url` that
  // the caller must navigate to in order to also end the Microsoft session.
  // local_only: true keeps the Microsoft session alive (trusted-device UX).
  const body = { token: accessToken }
  if (localOnly) {
    body.local_only = true
  }
  const response = await makePostRequest(
    `/accounts/auth/logout/`,
    body,
    true,
    'json'
  )
  return response
}

const forgotPassword = async (userData) => {
  const response = await makePostRequest(
    `/accounts/forgot-password/`,
    userData,
    false,
    'json'
  )
  return response
}

const resetPassword = async (uid, token, userData) => {
  const response = await makePostRequest(
    `/accounts/reset-password/?uid=${uid}&token=${token}`,
    userData,
    false,
    'json'
  )
  return response
}

const updatePassword = async (userData) => {
  const response = await makePostRequest(
    `/accounts/update-password/`,
    userData,
    true
  );
  return response
};

// ----------- Survey API
const getSurveyList = async () => {
  const data = await makeGetRequest(`/api/survey-list/`)
  return data
}



// Renamed from "getSurveys"
const updateSurveyStorage = async () => {
  const data = getSurveyList()
  Store.setState({ surveys: data })
}

const getSurveyJWT = async (jwt) => {
  const data = await makeGetRequest(`/api/get-survey-jwt/${jwt}`)
  return data
}

const createSurvey = async (body) => {
  try {
    console.log(body)
    const response = await makePostRequest('/surveys/', body, true, 'json');
    if (response.status !== 201) {
      if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        const errorMessages = Object.entries(response.data)
          .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
          .join('; ');
        throw new Error(errorMessages || 'Validation failed');
      }
    }
    const surveyData = response.data;
    if (!surveyData.survey_id || !surveyData.title) {
      throw new Error('Invalid assessment data received');
    }
    return {
      surveyId: surveyData.survey_id,
      title: surveyData.title,
      message: surveyData.message,
      success: true,
    };
  } catch (error) {
    console.error('Failed to create survey:', error.message);
    throw error;
  }
};

const getSurveys = async ({ page = 1, pageSize = 9, searchQuery = '', brief = false, released = null }) => {
  try {
    console.log('Fetching surveys with params:', { page, pageSize, searchQuery, brief });
    let url = `/surveys/?page=${page}&page_size=${pageSize}`;
    if (searchQuery) {
      url += `&search=${encodeURIComponent(searchQuery)}`;
    }
    if (brief) {
      url += `&brief=True`;
    }
    if (released !== null) {
      url += `&release=${released.toString().charAt(0).toUpperCase() + released.toString().slice(1)}`;
    }
    const response = await makeGetRequest(url, true);
    if (response.status !== 200) {
      throw new Error(`Failed to get assessments: ${response.data?.error || response.data?.message || 'Unknown error'}`);
    }

    const surveyData = response.data;
    if (!surveyData.results || !Array.isArray(surveyData.results)) {
      throw new Error('Invalid assessment data received: results array missing');
    }

    return {
      totalItems: surveyData.count,
      surveys: surveyData.results.map(survey => ({
        id: survey.id,
        title: survey.title,
        description: survey.description || `Gather feedback about ${survey.title.toLowerCase()}.`,
        emailsSent: survey.emailsSent,
        surveysOpened: survey.surveysOpened,
        surveysSubmitted: survey.surveysSubmitted,
        released: survey.released,
        created_at: survey.created_at,
        status: survey.status,
        is_publicly_available: survey.is_publicly_available,
        public_links: survey.public_links,
        insight_id: survey.insight_id,
        kind: survey.kind,
        respond_mode: survey.respond_mode || "traditional",
      })),
      next: surveyData.next,
      previous: surveyData.previous,
    };
  } catch (error) {
    console.error('Failed to get surveys:', error.message);
    throw error;
  }
};

const duplicateSurvey = async (surveyId) => {
  try {
    const response = await makePostRequest(`/surveys/${surveyId}/duplicate/`, null, true, 'json');
    if (response.status !== 201) {
      if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        const errorMessages = Object.entries(response.data)
          .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
          .join('; ');
        throw new Error(errorMessages || 'Validation failed');
      }
    }
    const surveyData = response.data;
    if (!surveyData.survey_id || !surveyData.title) {
      throw new Error('Invalid assessment data received');
    }
    return {
      surveyId: surveyData.survey_id,
      title: surveyData.title,
      message: surveyData.message,
      success: true,
    };
  } catch (error) {
    console.error('Failed to create survey:', error.message);
    throw error;
  }
}
const getSurvey = async (surveyId) => {

  try {
    const response = await makeGetRequest(`/surveys/${surveyId}`, true)
    if (response.status !== 200) {
      throw new Error(`Failed to get assessment: ${response.data?.error || 'Unknown error'}`);
    }
    const surveyData = response.data;
    return {
      id: surveyData.id,
      title: surveyData.title,
      description: surveyData.description || `Gather feedback about ${surveyData.title.toLowerCase()}.`,
      status: surveyData.status,
      release: surveyData.release,
      isPubliclyAvailable: surveyData.is_publicly_available,
      settings: surveyData.settings,
      createdAt: surveyData.created_at,
      updatedAt: surveyData.updated_at,
      released: surveyData.released,
      kind: surveyData.kind,
      questions: surveyData.questions || [],
      // The project this assessment is linked to (id, or {id, name}) and the
      // language it is written in; plus whether the link can still change —
      // see components/Project/projectLink.js.
      project: surveyData.project ?? null,
      // Whether the BE serves the link at all (an older BE does not).
      hasProjectLink: Object.prototype.hasOwnProperty.call(surveyData, "project"),
      language: surveyData.language || "en",
      // How respondents take it ("traditional" | "voice") and the voice a
      // voice survey reads its questions in (Survey.respond_mode / .voice).
      respondMode: surveyData.respond_mode || "traditional",
      voice: surveyData.voice || "",
      projectLocked: surveyData.project_locked,
      completedResponses: surveyData.completed_responses ?? surveyData.response_count,
    };
  } catch (error) {
    console.error('Failed to get survey:', surveyId, error.message);
    throw error;
  }
}

export const getRespondSurvey = async (surveyId, token) => {
  try {
    // NOTE: auth=false for public endpoint
    const res = await makeGetRequest(`/surveys/${surveyId}/respond/${token}/`, false);

    if (res.status !== 200) {
      const msg = res.data?.message || res.data?.error || "Unknown error";
      throw new Error(`Failed to load public assessment: ${msg}`);
    }

    return res.data?.data || res.data;
  } catch (error) {
    console.error('Failed to load public survey:', surveyId, error?.message || error);
    throw error;
  }
};

export const trackSurveyOpen = async (collectorId, token) => {
  try {
    // NOTE: auth=false for public endpoint
    const res = await makePostRequest(`/collector/${collectorId}/open/${token}/`, {}, false);

    if (res.status !== 200) {
      const msg = res.data?.message || res.data?.error || "Unknown error";
      console.warn(`Failed to track survey open: ${msg}`);
      return { success: false, error: msg };
    }

    return { success: true, data: res.data };
  } catch (error) {
    console.error(`Failed to track survey open:`, error.message);
    return { success: false, error: error.message };
  }
};

export const submitSurveyResponse = async (surveyId, token, responses) => {
  try {
    // responses is now already in the correct format: { responses: [...], completedAt: "..." }
    const res = await makePostRequest(`/surveys/${surveyId}/submit/${token}/`, responses, false, 'json');

    if (res.status !== 200 && res.status !== 201) {
      const msg = res.data?.message || res.data?.error || "Unknown error";
      throw new Error(`Failed to submit assessment: ${msg}`);
    }

    return res.data;
  } catch (error) {
    console.error('Failed to submit survey response for:', surveyId, error?.message || error);
    throw error;
  }
};

export const saveSurveyResponse = async (surveyId, token, answers, isSubmit = false) => {
  try {
    const res = await makePostRequest(`/surveys/${surveyId}/save/${token}/`, { answers, is_submit: isSubmit }, false, 'json');

    if (res.status !== 200 && res.status !== 201) {
      const msg = res.data?.message || res.data?.error || "Unknown error";
      throw new Error(`Failed to save assessment response: ${msg}`);
    }

    return res.data;
  } catch (error) {
    console.error('Failed to save survey response for:', surveyId, error?.message || error);
    throw error;
  }
};

// Transcribe a recorded audio blob for a public open-text survey answer.
// NOTE: token-gated public endpoint. The respondent's survey JWT travels in the
// URL path (decoded by HasValidSurveyToken on the BE), so auth=false — there is
// no Bearer header. The BE auto-detects the language, so only audio is sent.
// Audio is sent once and never stored client-side.
export const transcribeAudio = async (audioBlob, surveyId, token) => {
  try {
    if (!surveyId || !token) {
      throw new Error('transcribeAudio requires an assessment id and respondent token');
    }

    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');

    const res = await makePostFormRequest(`/collector/transcribe/${surveyId}/${token}/`, formData, false);

    if (!res.success || (res.status !== 200 && res.status !== 201)) {
      const msg = res.error || res.data?.error || res.data?.message || 'Transcription failed';
      throw new Error(msg);
    }

    return res.data;
  } catch (error) {
    console.error('Failed to transcribe audio:', error?.message || error);
    throw error;
  }
};

// Read a VOICE-mode survey's text aloud. POST /collector/tts/<survey_id>/<token>/
// { text } -> audio/mpeg. The token gates it like transcribe; the voice and accent
// are the survey's own (the BE ignores any client choice), and a traditional
// survey gets a 403. Resolves an audio Blob, rejects with .status/.code, and an
// aborted `signal` rejects with an AbortError.
export const fetchSurveyTts = async (surveyId, token, text, signal) => {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/collector/tts/${surveyId}/${token}/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
    signal,
  });
  if (!res.ok) {
    let detail;
    let code;
    try {
      const j = await res.json();
      code = j.code;
      detail = j.detail || j.error || j.message;
    } catch (e) {
      /* non-JSON error body */
    }
    const err = new Error(detail || 'Failed to synthesize speech');
    err.status = res.status;
    err.code = code;
    throw err;
  }
  return res.blob();
};

// The builder's Modern preview: read text aloud in the voice and language being
// edited. POST /collector/preview/tts/ { text, voice, language } -> audio/mpeg.
// Signed-in (the assessment may not be saved, so there is no respondent token);
// goes through axiosInstance for the 401 refresh. Same contract as fetchSurveyTts.
export const fetchSurveyPreviewTts = async (text, voice, language, signal) => {
  try {
    const res = await axiosInstance.post(
      '/collector/preview/tts/',
      { text, voice, language },
      { headers: { ...getHeaders(true), 'Content-Type': 'application/json' }, responseType: 'blob', signal }
    );
    return res.data;
  } catch (e) {
    if (axios.isCancel(e)) {
      const abort = new Error('Aborted');
      abort.name = 'AbortError';
      throw abort;
    }
    const err = new Error('Failed to synthesize speech');
    err.status = e?.response?.status;
    throw err;
  }
};

// Transcribe a spoken answer in the builder's Modern preview, in the language
// being edited. POST /collector/preview/transcribe/ (signed-in). Resolves { text }.
export const transcribePreviewAudio = async (audioBlob, language) => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  if (language) formData.append('language', language);
  const res = await makePostFormRequest('/collector/preview/transcribe/', formData, true);
  if (!res.success) throw new Error(res.data?.error || res.error || 'Transcription failed');
  return res.data;
};

export const getCollectorRespondents = async (surveyId = null, collectorId = null, page = 1, pageSize = 12, ordering = '-started_at') => {
  try {
    let url = '/collector/respondents/';
    const params = new URLSearchParams();

    if (surveyId) params.append('survey_id', surveyId);
    if (collectorId) params.append('collector_id', collectorId);
    if (page) params.append('page', page);
    if (pageSize) params.append('page_size', pageSize);
    if (ordering) params.append('ordering', ordering);

    if (params.toString()) {
      url += '?' + params.toString();
    }

    const response = await makeGetRequest(url, true);

    if (response.status !== 200) {
      throw new Error(`Failed to fetch respondents: ${response.data?.error || 'Unknown error'}`);
    }

    return response.data;
  } catch (error) {
    console.error('Failed to fetch collector respondents:', error?.message || error);
    throw error;
  }
};

export const getCollectorRespondentDetail = async (respondentId) => {
  try {
    const response = await makeGetRequest(`/collector/respondents/${respondentId}/`, true);

    if (response.status !== 200) {
      throw new Error(`Failed to fetch respondent details: ${response.data?.error || 'Unknown error'}`);
    }

    return response.data;
  } catch (error) {
    console.error('Failed to fetch respondent details for:', respondentId, error?.message || error);
    throw error;
  }
};

const updateSurvey = async (surveyId, body) => {
  try {
    console.log("Updating survey with body:", body);
    const response = await makePutRequest(`/surveys/${surveyId}/`, body, true);
    if (response.status !== 200) {
      if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        const errorMessages = Object.entries(response.data)
          .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
          .join('; ');
        throw new Error(errorMessages || 'Validation failed');
      };
    }
    const surveyData = response.data.data;

    console.log(surveyData)
    if (!surveyData) {
      throw new Error('Invalid assessment data received');
    }
    return {
      surveyId: surveyData.survey_id,
      title: surveyData.title,
      message: response.data.message,
      status: response.data.status,
      questions: surveyData.questions || [],
      settings: surveyData.settings || {},
      updatedAt: surveyData.updated_at || new Date().toISOString()
    };
  } catch (error) {
    console.error('Failed to update survey:', error.message);
    throw error;
  }
};

const lockSurvey = async (surveyId, isLocked) => {
  try {
    console.log(`${isLocked ? 'Locking' : 'Unlocking'} survey ${surveyId}`);
    const response = await makePutRequest(`/surveys/${surveyId}/`, { release: isLocked }, true);
    if (response.status !== 200) {
      if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        const errorMessages = Object.entries(response.data)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
          .join('; ');
        throw new Error(errorMessages || 'Validation failed');
      }
      throw new Error(`Failed to ${isLocked ? 'lock' : 'unlock'} assessment`);
    }
    const surveyData = response.data.data;
    if (!surveyData) {
      throw new Error('Invalid assessment data received');
    }
    return {
      surveyId: surveyData.survey_id,
      title: surveyData.title,
      message: response.data.message,
      release: surveyData.release,
      success: true
    };
  } catch (error) {
    console.error(`Failed to ${isLocked ? 'lock' : 'unlock'} survey:`, error.message);
    throw error;
  }
};

const deleteSurvey = async (id) => {
  try {
    const response = await makeDeleteRequest(`/surveys/${id}/`, true);
    if (response.status !== 200) {
      throw new Error(`${response.data.message || 'Failed to delete assessment'}`);
    }
    return {
      message: response.data.message,
      success: response.data.success
    };
  } catch (error) {
    console.error('Failed to delete survey:', error.message);
    throw error;
  }
};

const getSurveyListPage = async (page, resultsPerPage) => {
  const data = await makeGetRequest(
    `/api/survey-list-page/${page}/${resultsPerPage}`
  )
  Store.setState({
    surveys: data.items,
    surveyPage: page,
    surveysPerPage: resultsPerPage
  })
  return data
}

// ----------- Collector API
const createCollector = async (body) => {
  try {
    console.log(body)
    const response = await makePostRequest('/collector/', body, true, 'json');
    if (response.status !== 201) {
      if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        const errorMessages = Object.entries(response.data)
          .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
          .join('; ');
        throw new Error(errorMessages || 'Validation failed');
      }
    }
    const collectorData = response.data;
    if (!collectorData.collector_id || !collectorData.name) {
      throw new Error('Invalid collector data received');
    }
    return {
      collectorId: collectorData.collector_id,
      name: collectorData.name,
      message: collectorData.message,
      success: true,
    };
  } catch (error) {
    console.error('Failed to create collector:', error.message);
    throw error;
  }
}


const getCollector = async (surveyId) => {

  try {
    const response = await makeGetRequest(`/collector/${surveyId}`, true)
    if (response.status !== 200) {
      throw new Error(`Failed to get collector: ${response.data?.error || 'Unknown error'}`);
    }
    return response.data;
  } catch (error) {
    console.error('Failed to get collector for:', surveyId, error.message);
    throw error;
  }
}

const getCollectorById = async (surveyId, collectorId) => {
  try {
    const response = await makeGetRequest(`/collector/${surveyId}/${collectorId}/`, true)
    if (response.status !== 200) {
      throw new Error(`Failed to get collector: ${response.data?.error || 'Unknown error'}`);
    }
    const collectorData = response.data;

    console.log("collectorData: ", collectorData);

    const transformedEmailRecipients = Object.entries(collectorData.email_recipients || {}).map(([id, recipient]) => ({
      id: id,
      email: recipient.email,
      firstName: recipient.first_name || "",
      isValid: recipient.is_valid !== undefined ? recipient.is_valid : true
    }));
    return {
      collectorId: collectorData.id,
      name: collectorData.name,
      type: collectorData.type ? collectorData.type.toUpperCase() : "EMAIL",
      emailRecipients: transformedEmailRecipients,
      fromEmail: collectorData.from_email,
      emailSubject: collectorData.email_subject,
      emailHeader: collectorData.email_header,
      emailBody: collectorData.email_body,
      showSurveyButton: collectorData.show_survey_button !== undefined ? collectorData.show_survey_button : true,
      surveyButtonText: collectorData.survey_button_text,
      success: true,
      link: collectorData.link,
    };
  } catch (error) {
    console.error('Failed to get collector for:', surveyId, error.message);
    throw error;
  }
}

const updateCollector = async (surveyId, collectorId, body) => {
  try {
    console.log("Updating collector with body:", body);
    const response = await makePutRequest(`/collector/${surveyId}/${collectorId}/`, body, true);
    if (response.status !== 200) {
      if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        const errorMessages = Object.entries(response.data)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
          .join('; ');
        throw new Error(errorMessages || 'Validation failed');
      }
      throw new Error('Failed to update collector');
    }
    const collectorData = response.data;

    console.log(collectorData);
    if (!collectorData || !collectorData.collector_id || !collectorData.name) {
      throw new Error('Invalid collector data received');
    }
    return {
      collectorId: collectorData.collector_id,
      surveyId: parseInt(surveyId),
      name: collectorData.name,
      message: collectorData.message
    };
  } catch (error) {
    console.error('Failed to update collector:', error.message);
    throw error;
  }
}

const deleteCollector = async (surveyId, collectorId) => {
  try {
    const response = await makeDeleteRequest(`/collector/${surveyId}/${collectorId}/`, true);
    if (response.status !== 200) {
      throw new Error(`Failed to delete collector: ${response.data?.error || 'Unknown error'}`);
    }
    console.log("Delete Response:", response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to delete collector for:', surveyId, collectorId, error.message);
    throw error;
  }
};

const retryEmails = async () => {
  console.log("retryEmails called.")
};

const retryEmail = async (surveyId, collectorId, emailLogId, body) => {
  try {
    console.log("retryEmail called for:", { surveyId, collectorId, emailLogId, body });
    const response = await makePostRequest(
      `/collector/retry-email/${surveyId}/${collectorId}/${emailLogId}/`,
      body,
      true,
      "json"
    );
    if (response.status !== 200) {
      throw new Error(`Failed to retry email: ${response.data?.error || 'Unknown error'}`);
    }
    console.log("Retry Email Response:", response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to retry email for:', surveyId, collectorId, emailLogId, error.message);
    throw error;
  }
};

const getFailedEmails = async (surveyId, collectorId) => {
  try {
    const response = await makeGetRequest(`/collector/failed-emails/${surveyId}/${collectorId}/`, true);
    if (response.status !== 200) {
      throw new Error(`Failed to fetch collector status: ${response.data?.error || 'Unknown error'}`);
    }
    console.log("Collector Status Response:", response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch collector status for:', surveyId, collectorId, error.message);
    throw error;
  }
}


const getCollectorStatus = async (surveyId, collectorId) => {
  try {
    const response = await makeGetRequest(`/collector/status/${surveyId}/${collectorId}/`, true);
    if (response.status !== 200) {
      throw new Error(`Failed to fetch collector status: ${response.data?.error || 'Unknown error'}`);
    }
    console.log("Collector Status Response:", response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch collector status for:', surveyId, collectorId, error.message);
    throw error;
  }
};

const updateEmail = async (surveyId, collectorId, emailLogId, body) => {
  try {
    console.log("update email called: ", body)
    const response = await makePutRequest(
      `/collector/email/${surveyId}/${collectorId}/${emailLogId}/`,
      body,
      true
    );
    if (response.status !== 200) {
      throw new Error(`Failed to update email: ${response.data?.error || 'Unknown error'}`);
    }
    console.log("Update Email Response:", response.data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Failed to update email for:', surveyId, collectorId, emailLogId, error.message);
    return { success: false, error: error.message };
  }
};

const saveCollector = async (surveyId, collectorId, body) => {
  const data = await makePutRequest(
    `/api/collector/${surveyId}/${collectorId}/`,
    body
  )
  return data
}

export const duplicateCollector = async (surveyId, collectorId) => {
  try {
    const response = await makePostRequest(`/collector/${surveyId}/${collectorId}/duplicate/`, null, true, 'json');
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Failed to duplicate collector: ${response.data?.error || 'Unknown error'}`);
    }
    return response.data;
  } catch (error) {
    console.error('Failed to duplicate collector:', collectorId, error?.message || error);
    throw error;
  }
};

// The invitation email's AI helpers ("Write for me" / "Polish") and preview.
// Neither saves: both take the editor's current, possibly unsaved, draft.
// Resolves to { ok, data, status, code } instead of throwing, so the page can
// tell a timeout / rate limit / rejected output apart for its error message.
const collectorEmailPost = async (surveyId, collectorId, action, body) => {
  const response = await makePostRequest(`/collector/${surveyId}/${collectorId}/${action}/`, body, true, 'json');
  const ok = response.status === 200;
  return {
    ok,
    data: ok ? response.data : null,
    status: response.status,
    code: ok ? null : (response.data?.code || null),
  };
};

export const emailAssist = (surveyId, collectorId, { mode, tone, subject, body }) =>
  collectorEmailPost(surveyId, collectorId, 'email-assist', {
    mode,
    ...(tone ? { tone } : {}),
    subject: subject || '',
    body: body || '',
  });

export const emailPreview = (surveyId, collectorId, { subject, body, showSurveyButton, surveyButtonText }) =>
  collectorEmailPost(surveyId, collectorId, 'email-preview', {
    subject: subject || '',
    body: body || '',
    show_survey_button: showSurveyButton !== false,
    survey_button_text: surveyButtonText || '',
  });

export const renameCollector = async (surveyId, collectorId, name) => {
  try {
    const response = await makePatchRequest(`/collector/${surveyId}/${collectorId}/rename/`, { name }, true);
    if (response.status !== 200) {
      throw new Error(`Failed to rename collector: ${response.data?.error || 'Unknown error'}`);
    }
    return response.data;
  } catch (error) {
    console.error('Failed to rename collector:', collectorId, error?.message || error);
    throw error;
  }
};

/**
 * @param {string} collectorId
 * @param {{ scheduledTime?: string } | undefined} [options] — When scheduling, `scheduledTime` is local wall time "YYYY-MM-DD HH:mm" (no timezone); omit or leave unset for send-now.
 */
export const sendReminderEmail = async (collectorId, options) => {
  try {
    const body =
      options?.scheduledTime != null && options.scheduledTime !== ''
        ? { scheduled_time: options.scheduledTime }
        : {};
    const response = await makePostRequest(`/collector/reminder/${collectorId}/`, body, true, 'json');
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Failed to send reminder: ${response.data?.error || 'Unknown error'}`);
    }
    return response.data;
  } catch (error) {
    console.error('Failed to send reminder for collector:', collectorId, error?.message || error);
    throw error;
  }
};

const collectSurvey = async (id, collector_id, emails) => {
  const data = await makePostRequest(
    `/api/collect-survey/${id}/${collector_id}/`,
    emails
  )
  return data
}

const collectPublicSurvey = async (surveyId, collectorId) => {
  const data = await makePostRequest(
    `/api/collect-public-survey/${surveyId}/${collectorId}/`,
    {}
  )
  await updateSurveyStorage()
  return data
}

const collectorLock = async (collector_id, locked) => {
  const data = await makePostRequest(`/api/collector-lock/${collector_id}`, {
    locked
  })
  updateSurveyStorage()
  return data
}

const getCollectorsForSurvey = async (s_id) => {
  const data = await makeGetRequest(`/api/get-collectors-for-survey/${s_id}`)
  return data
}

const getCollectorsForUser = async () => {
  const data = await makeGetRequest(`/collector/`, true)
  return data
}

// ----------- Survey Response API
const saveAnswer = async (jwt, body) => {
  const { data, status } = await makePostRequest(
    `/api/save-answer/${jwt}`,
    body
  )

  switch (status) {
    case 200:
      return { success: true, message: 'Assessment submitted successfully!' }
    case 400:
      console.error('saveSurvey Error: ', data)
      return { success: false, message: data.msg }
    case 423:
      console.error('saveSurvey Error: ', data)
      return { success: false, message: data.msg }
    default:
      console.error('saveSurvey Unexpected Error: ', data)
      return { success: false, message: 'An unexpected error occurred.' }
  }
}

const saveAnswerTmp = async (jwt, body) => {
  await makePostRequest(`/api/save-answer-tmp/${jwt}`, body)
}

const getResponsesForCollector = async (survey_id, collector_id) => {
  const data = await makeGetRequest(
    `/api/get-responses-for-collector/${survey_id}/${collector_id}`
  )
  return data
}

// ----------- Emails API
const scheduleFunc = async () => {
  const data = await makeGetRequest(`/api/schedule-func/`)
  return data
}

const sendTestEmail = async (body) => {
  const data = await makePutRequest(`/api/test-email/`, body)
  return data
}

// ----------- Downloads API
const collectAll = async (id) => {
  const data = await makeGetRequest(`/api/collect-all/${id}`)
  try {
    fileDownload(data, `prompta_output_${Date.now().toString()}.csv`)
  } catch (e) {
    console.log(e)
  }
  return data
}

const collectLikert = (id) =>
  axios
    .get(`/api/collect-likert/${id}`, {
      responseType: 'blob'
    })
    .then((res) => {
      fileDownload(
        res.data,
        `prompta_output_likert_xlsx_${Date.now().toString()}.xlsx`
      )
    })

const collectDemographic = (id) =>
  axios
    .get(`/api/collect-demographic/${id}`, {
      responseType: 'blob'
    })
    .then((res) => {
      fileDownload(
        res.data,
        `prompta_output_demog_xlsx_${Date.now().toString()}.xlsx`
      )
    })

const getCurrentUser = async () => {
  const data = await makeGetRequest(
    '/accounts/me/',
    true
  )
  return data
}

// ----------- Settings API
const getUserProfile = async (username) => {
  const data = await makeGetRequest(
    '/profiles/me/',
    {
      user: username
    },
    true
  )
  return data
}

const updateUserProfile = async (newProfile) => {
  const data = await makePutRequest('/profiles/me/', newProfile, true)
  return data
}

const getActiveSessionData = async () => {
  const data = await makeGetRequest('/accounts/active-session/', true)
  return data
}

const logoutActiveSession = async (session_id) => {
  const data = await makeDeleteRequest(
    `/accounts/active-session/${session_id}/`,
    true
  )
  return data
}

const deleteAccount = async () => {
  const data = await makeDeleteRequest(
    `/accounts/deactivate-account/`,
    true
  )
  return data
}


const logoutAllActiveSessions = async (session_ids) => {
  const data = await makeDeleteRequest(
    `/accounts/active-session/`,
    true,
    session_ids
  )
  return data
}

// ----------- Organization API
const getOrganizationUsers = async () => {
  const data = await makeGetRequest(`/accounts/users/`, true)
  return data
}

const addOrganizationUser = async (userData) => {
  console.log("Inside addOrganizationUser userdata:", userData)
  const data = await makePostRequest('/accounts/users/', userData, true)
  return data
}

const removeOrganizationUsers = async (user_ids) => {
  console.log(user_ids)
  const response = await makeDeleteRequest('/accounts/users/', true, { user_ids });
  return response;
};

// ----------- Misc API
const getToken = async () => {
  const { token } = await makeGetRequest(`/api/get-token/`)
  return token
}

const uploadImage = async (imageFile) => {
  const dt = new FormData()
  dt.append('image', imageFile)
  const imageData = await makePostFormRequest(`/api/upload-image/`, dt)
  return imageData
}

const generateWebLink = async (id, collector_id, email) => {
  const data = await makeGetRequest(
    `/api/generate-weblink/${id}/${collector_id}/${email}`
  )
  return data
}

const generatePublicWebLink = async (survey_id, collector_id) => {
  const data = await makeGetRequest(
    `/api/generate-public-weblink/${survey_id}/${collector_id}`
  )
  return data
}

const healthCheck = async () => {
  const data = await makeGetRequest('/health', true)
  return data
}

const getCountryCode = async () => {
  try {
    const response = await makeGetRequest(`/accounts/country-code/`, true); // Assuming auth is needed
    if (response.status !== 200) {
      throw new Error(`Failed to fetch country code: ${response.data?.message || 'Unknown error'}`);
    }
    const countryCode = response.data;
    if (typeof countryCode !== 'string' || countryCode.length !== 2) {
      throw new Error('Invalid country code received');
    }
    return countryCode;
  } catch (error) {
    console.error('Failed to fetch country code:', error.message);
    throw error; // Let the caller handle the error
  }
};

// ------------- Subscriptions API
const getSubscriptionPlans = async () => {
  try {
    const response = await makeGetRequest(`/subscriptions/plans/`, true);
    if (response.status !== 200) {
      throw new Error(`Failed to fetch subscription plans: ${response.data?.message || 'Unknown error'}`);
    }
    const plans = Array.isArray(response.data) ? response.data : [];
    return plans;
  } catch (error) {
    console.error('Failed to fetch subscription plans:', error.message);
    throw error;
  }
};

const subscribePlan = async (body) => {
  try {
    const response = await makePostRequest('/subscriptions/subscribe/', body, true);
    if (response.status !== 200) {
      throw new Error(`Failed to subscribe: ${response.data?.message || 'Unknown error'}`);
    }
    return response.data;
  } catch (error) {
    throw error;
  }
};

const verifyCheckoutSession = async (body) => {
  try {
    const response = await makePostRequest('/subscriptions/verify/', body, true);
    if (response.status !== 200) {
      throw new Error(`Failed to verify subscription: ${response.data?.error || 'Unknown error'}`);
    }
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getUserSubscription = async () => {
  try {
    const response = await makeGetRequest(`/subscriptions/user/`, true);
    if (response.status === 404 || response.status === 400) {
      return { hasSubscription: false, message: "You haven't subscribed to a plan yet." };
    }
    if (response.status !== 200) {
      throw new Error(`Failed to fetch subscription plans: ${response.data?.error || 'Unknown error'}`);
    }
    const plans = response.data;
    return { hasSubscription: true, data: plans };
  } catch (error) {
    console.error('Failed to fetch subscription plans:', error.message);
    throw error;
  }
};

const getUserSubscriptionUsage = async () => {
  try {
    const response = await makeGetRequest('/subscriptions/customers/usage/', true);
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch subscription usage');
    }
    return { hasSubscription: true, data: response.data.data };
  } catch (error) {
    console.error('Failed to fetch subscription usage:', error.message);
    throw error;
  }
};

const getPaymentMethods = async () => {
  try {
    const response = await makeGetRequest('/subscriptions/payment-methods/', true);
    if (response.status === 404) {
      return { success: false, message: 'No payment methods found.' };
    }
    if (response.status !== 200) {
      throw new Error(`Failed to fetch payment methods: ${response.data?.error || 'Unknown error'}`);
    }
    const paymentMethods = Array.isArray(response.data.data) ? response.data : [];
    return paymentMethods;
  } catch (error) {
    console.error('Failed to fetch payment methods:', error.message);
    throw error;
  }
};

const setDefaultPaymentMethod = async (data) => {
  try {
    const response = await makePostRequest('/subscriptions/payment-methods/default/', data, true);
    if (response.status !== 200) {
      throw new Error(`${response.data?.error || 'Unknown error'}`);
    }
    return response.data;
  } catch (error) {
    console.error('API error:', error);
    throw error.response?.data || { success: false, error: 'Network error' };
  }
};

const updatePaymentMethod = async () => {
  try {
    const response = await makePutRequest(`/subscriptions/update-payment-method/`, {}, true);
    if (response.status !== 200) {
      throw new Error(`Failed to initiate payment method update: ${response.data?.error || 'Unknown error'}`);
    }
    const result = response.data;
    if (!result.success || !result.data?.checkout_url) {
      throw new Error(result.error || 'Failed to initiate payment method update: No checkout URL provided');
    }
    return result;
  } catch (error) {
    console.error('Failed to initiate payment method update:', error.message);
    throw error;
  }
}

const confirmPaymentMethodUpdate = async (body) => {
  try {
    const response = await makePostRequest(`/subscriptions/confirm-payment-method-update/`, body, true);
    if (response.status !== 200) {
      throw new Error(`Failed to confirm payment method update: ${response.data?.error || 'Unknown error'}`);
    }
    const result = response.data;
    if (!result.success) {
      throw new Error(result.error || 'Failed to confirm payment method update');
    }
    return result;
  } catch (error) {
    console.error('Failed to confirm payment method update:', error.message);
    throw error;
  }
}

const deletePaymentMethod = async (data) => {
  try {
    const response = await makeDeleteRequest(`/subscriptions/delete-payment-method/`, true, data);
    if (response.status !== 204) {
      throw new Error(`Failed to delete payment method: ${response.data?.error || 'Unknown error'}`);
    }
    return { success: true };
  } catch (error) {
    console.error('Failed to delete payment method:', error.message);
    throw error;
  }
};

const cancelSubscription = async () => {
  try {
    const response = await makeDeleteRequest('/subscriptions/cancel-active-subscription/', true);
    if (!response.success) {
      return {
        success: false,
        message: response.error || 'Failed to cancel subscription',
        data: response.data,
        status: response.status,
      };
    }
    return {
      success: true,
      message: response.data?.message || 'Subscription canceled successfully',
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    console.error('Failed to cancel subscription:', error.message);
    return {
      success: false,
      message: error.message || 'An unexpected error occurred',
      data: {},
      status: null,
    };
  }
};

// ----------- Insights API
const getInsightConfig = async (configId) => {
  try {
    const response = await makeGetRequest(`/insights/configs/${configId}/`, true);
    if (response.status !== 200) {
      throw new Error(`Failed to get insight config: ${response.data?.error || 'Unknown error'}`);
    }
    return response.data;
  } catch (error) {
    console.error('Failed to get insight config for:', configId, error.message);
    throw error;
  }
};

const updateInsightConfig = async (insightId, metadata) => {
  try {
    const response = await makePutRequest(`/insights/configs/${insightId}/edit/`, { metadata }, true);
    if (response.status !== 200) {
      throw new Error(`Failed to update insight config: ${response.data?.error || 'Unknown error'}`);
    }
    return response.data;
  } catch (error) {
    console.error('Failed to update insight config for:', insightId, error.message);
    throw error;
  }
};

const generatePreliminaryReport = async (surveyId) => {
  try {
    const url = `/insights/${surveyId}/generate-preliminary-report/`;
    const response = await makeGetRequest(url, true);
    if (response.status !== 200 && response.status !== 202) {
      throw new Error(`Failed to generate preliminary report: ${response.data?.error || response.data?.message || 'Unknown error'}`);
    }
    return response.data;
  } catch (error) {
    console.error('Failed to generate preliminary report for survey:', surveyId, error.message);
    throw error;
  }
};

const generateInsightReport = async (insightId, name = null) => {
  try {
    let url = `/insights/${insightId}/generate-report/`;
    if (name) {
      const queryParams = new URLSearchParams();
      queryParams.append('name', name);
      url += `?${queryParams.toString()}`;
    }
    const response = await makeGetRequest(url, true);
    if (response.status !== 200) {
      throw new Error(`Failed to generate report: ${response.data?.error || 'Unknown error'}`);
    }
    return response.data;
  } catch (error) {
    console.error('Failed to generate report for insight:', insightId, error.message);
    throw error;
  }
};

const api = {
  getActiveSessionData,
  addOrganizationUser,
  collectAll,
  collectDemographic,
  collectLikert,
  collectorLock,
  collectPublicSurvey,
  collectSurvey,
  createCollector,
  getCollector,
  getCollectorById,
  emailAssist,
  emailPreview,
  updateCollector,
  deleteCollector,
  retryEmails,
  forgotPassword,
  generatePublicWebLink,
  generateWebLink,
  getCollectorsForSurvey,
  getCollectorsForUser,
  getCountryCode,
  getCSRF,
  getOrganizationUsers,
  getResponsesForCollector,
  getSurvey,
  getSurveyJWT,
  getSurveyList,
  getSurveyListPage,
  getToken,
  getUserProfile,
  healthCheck,
  login,
  authLookup,
  ssoStart,
  logout,
  logoutActiveSession,
  logoutAllActiveSessions,
  registerUser,
  removeOrganizationUsers,
  resetPassword,
  saveAnswer,
  saveAnswerTmp,
  saveCollector,
  duplicateCollector,
  renameCollector,
  createSurvey,
  duplicateSurvey,
  getSurveys,
  updateSurvey,
  lockSurvey,
  deleteSurvey,
  scheduleFunc,
  sendTestEmail,
  updateSurveyStorage,
  updateUserProfile,
  uploadImage,
  updatePassword,
  getCurrentUser,
  deleteAccount,
  getSubscriptionPlans,
  subscribePlan,
  verifyCheckoutSession,
  getUserSubscription,
  getPaymentMethods,
  setDefaultPaymentMethod,
  updatePaymentMethod,
  confirmPaymentMethodUpdate,
  deletePaymentMethod,
  getUserSubscriptionUsage,
  cancelSubscription,
  getCollectorStatus,
  getFailedEmails,
  retryEmail,
  updateEmail,
  getRespondSurvey,
  trackSurveyOpen,
  submitSurveyResponse,
  saveSurveyResponse,
  transcribeAudio,
  fetchSurveyTts,
  fetchSurveyPreviewTts,
  transcribePreviewAudio,
  getCollectorRespondents,
  getCollectorRespondentDetail,
  getInsightConfig,
  updateInsightConfig,
  generatePreliminaryReport,
  generateInsightReport,
  sendReminderEmail
}

export default api
